// @ts-nocheck
import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from '../store';
import * as api from '../api';
import type { Column } from '../types';
import ContextMenu, { type MenuItem } from './ContextMenu';

const LABEL_COLORS = [
  '#00c875', '#fdab3d', '#e2445c', '#a25ddc', '#579bfc',
  '#ff642e', '#ffcb00', '#c4c4c4', '#9aadbd', '#ff158a',
  '#037f4c', '#0086c0', '#bb3354', '#175a63', '#4eccc6',
  '#333333', '#7f5347', '#225091', '#bca58a', '#cab641',
];

interface Props {
  column: Column;
  onDragStart?: (e: React.DragEvent, columnId: string) => void;
  onDragOver?: (e: React.DragEvent, columnId: string) => void;
  onDrop?: (e: React.DragEvent, columnId: string) => void;
  onDragEnd?: () => void;
  dragOverId?: string | null;
}

export default function ColumnHeader({ column, onDragStart, onDragOver, onDrop, onDragEnd, dragOverId }: Props) {
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(column.name);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);
  const [showLabelEditor, setShowLabelEditor] = useState(false);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const isLabelColumn = column.type === 'status' || column.type === 'priority';

  const handleSave = async () => {
    if (!name.trim()) { setName(column.name); setEditing(false); return; }
    if (name.trim() !== column.name) {
      dispatch({ type: 'UPDATE_COLUMN', id: column.id, data: { name: name.trim() } });
      await api.updateColumn(column.id, { name: name.trim() });
    }
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!confirm(`Delete column "${column.name}"?`)) return;
    await api.deleteColumn(column.id);
    dispatch({ type: 'REMOVE_COLUMN', id: column.id });
  };

  const handleClone = async () => {
    const cloned = await api.cloneColumn(column.id);
    dispatch({ type: 'ADD_COLUMN', column: { ...cloned, config: typeof cloned.config === 'string' ? cloned.config : JSON.stringify(cloned.config) } });
  };

  const menuItems: MenuItem[] = [
    { label: 'Rename', onClick: () => { setName(column.name); setEditing(true); } },
    ...(isLabelColumn ? [{ label: 'Edit labels', onClick: () => setShowLabelEditor(true) }] : []),
    { label: 'Clone column', onClick: handleClone },
    { divider: true, label: '' },
    { label: 'Delete column', danger: true, onClick: handleDelete },
  ];

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = column.width || 150;
    resizeRef.current = { startX, startWidth };

    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const diff = ev.clientX - resizeRef.current.startX;
      const newWidth = Math.max(80, resizeRef.current.startWidth + diff);
      dispatch({ type: 'UPDATE_COLUMN', id: column.id, data: { width: newWidth } });
    };

    const handleMouseUp = (ev: MouseEvent) => {
      if (resizeRef.current) {
        const diff = ev.clientX - resizeRef.current.startX;
        const newWidth = Math.max(80, resizeRef.current.startWidth + diff);
        api.updateColumn(column.id, { width: newWidth });
      }
      resizeRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [column.id, column.width, dispatch]);

  const isDragOver = dragOverId === column.id;

  return (
    <th
      style={{ width: column.width, minWidth: 80, position: 'sticky', top: 118 }}
      onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY }); }}
      draggable={!editing}
      onDragStart={(e) => onDragStart?.(e, column.id)}
      onDragOver={(e) => { e.preventDefault(); onDragOver?.(e, column.id); }}
      onDrop={(e) => onDrop?.(e, column.id)}
      onDragEnd={() => onDragEnd?.()}
    >
      {isDragOver && (
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
          background: '#0073ea', zIndex: 5,
        }} />
      )}
      <div className="col-header">
        <span className="col-drag-handle" style={{ cursor: editing ? 'default' : 'grab', marginRight: 2, color: '#c3c6d4', fontSize: 10 }}>⠿</span>
        {editing ? (
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
            style={{ border: 'none', background: '#e8e8ff', outline: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 13, width: '100%' }}
          />
        ) : (
          <span onClick={() => { setName(column.name); setEditing(true); }} style={{ cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {column.name}
          </span>
        )}
        <button
          className="col-header-menu"
          onClick={(e) => { e.stopPropagation(); setCtxMenu({ x: e.clientX, y: e.clientY }); }}
        >
          ▾
        </button>
      </div>
      {/* Resize handle */}
      <div
        className="col-resize-handle"
        onMouseDown={handleResizeStart}
      />
      {ctxMenu && (
        <ContextMenu x={ctxMenu.x} y={ctxMenu.y} items={menuItems} onClose={() => setCtxMenu(null)} />
      )}
      {showLabelEditor && createPortal(
        <LabelEditor column={column} onClose={() => setShowLabelEditor(false)} />,
        document.body
      )}
    </th>
  );
}

// ──────────────────── LABEL EDITOR ────────────────────
function LabelEditor({ column, onClose }: { column: Column; onClose: () => void }) {
  const dispatch = useDispatch();
  const config = (() => {
    try { return typeof column.config === 'string' ? JSON.parse(column.config) : column.config; }
    catch { return { labels: {}, colors: {} }; }
  })();

  const [labels, setLabels] = useState<Record<string, string>>({ ...config.labels });
  const [colors, setColors] = useState<Record<string, string>>({ ...config.colors });
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('#579bfc');
  const [editingColor, setEditingColor] = useState<string | null>(null);

  const handleSave = async () => {
    const newConfig = { labels, colors };
    const configStr = JSON.stringify(newConfig);
    dispatch({ type: 'UPDATE_COLUMN', id: column.id, data: { config: configStr } });
    await api.updateColumn(column.id, { config: newConfig } as any);
    onClose();
  };

  const handleAdd = () => {
    if (!newLabel.trim()) return;
    const key = newLabel.trim().toLowerCase().replace(/\s+/g, '_');
    if (labels[key] !== undefined) return;
    setLabels({ ...labels, [key]: newLabel.trim() });
    setColors({ ...colors, [key]: newColor });
    setNewLabel('');
    setNewKey('');
    setNewColor(LABEL_COLORS[Object.keys(labels).length % LABEL_COLORS.length]);
  };

  const handleRemove = (key: string) => {
    if (key === '') return; // don't remove the empty/default status
    const newLabels = { ...labels };
    const newColors = { ...colors };
    delete newLabels[key];
    delete newColors[key];
    setLabels(newLabels);
    setColors(newColors);
  };

  const handleLabelChange = (key: string, val: string) => {
    setLabels({ ...labels, [key]: val });
  };

  const handleColorChange = (key: string, color: string) => {
    setColors({ ...colors, [key]: color });
    setEditingColor(null);
  };

  const entries = Object.entries(labels).filter(([k]) => k !== '');

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ position: 'relative', maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&#10005;</button>
        <h2>Edit Labels - {column.name}</h2>

        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Default empty status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: '#f6f7fb', borderRadius: 6 }}>
            <div
              style={{ width: 28, height: 28, borderRadius: 6, background: colors[''] || '#c4c4c4', flexShrink: 0, cursor: 'pointer' }}
              onClick={() => setEditingColor(editingColor === '' ? null : '')}
            />
            <span style={{ fontSize: 13, color: '#676879', fontStyle: 'italic' }}>(empty / default)</span>
          </div>
          {editingColor === '' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '8px', background: '#f6f7fb', borderRadius: 6 }}>
              {LABEL_COLORS.map((c) => (
                <div key={c} onClick={() => handleColorChange('', c)}
                  style={{ width: 24, height: 24, borderRadius: '50%', background: c, cursor: 'pointer', border: c === colors[''] ? '2px solid #323338' : '2px solid transparent' }}
                />
              ))}
            </div>
          )}

          {/* Existing labels */}
          {entries.map(([key, label]) => (
            <div key={key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: '#f6f7fb', borderRadius: 6 }}>
                <div
                  style={{ width: 28, height: 28, borderRadius: 6, background: colors[key] || '#c4c4c4', flexShrink: 0, cursor: 'pointer' }}
                  onClick={() => setEditingColor(editingColor === key ? null : key)}
                />
                <input
                  value={label as string}
                  onChange={(e) => handleLabelChange(key, e.target.value)}
                  style={{ flex: 1, border: '1px solid #e6e9ef', borderRadius: 6, padding: '6px 10px', fontSize: 13, outline: 'none' }}
                />
                <button onClick={() => handleRemove(key)} style={{ color: '#e2445c', fontSize: 16, padding: '2px 6px' }}>
                  &#10005;
                </button>
              </div>
              {editingColor === key && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '8px', background: '#f6f7fb', borderRadius: '0 0 6px 6px' }}>
                  {LABEL_COLORS.map((c) => (
                    <div key={c} onClick={() => handleColorChange(key, c)}
                      style={{ width: 24, height: 24, borderRadius: '50%', background: c, cursor: 'pointer', border: c === colors[key] ? '2px solid #323338' : '2px solid transparent' }}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add new label */}
        <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <div
            style={{ width: 28, height: 28, borderRadius: 6, background: newColor, flexShrink: 0, cursor: 'pointer' }}
            onClick={() => setEditingColor(editingColor === '__new__' ? null : '__new__')}
          />
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            placeholder="New label name..."
            style={{ flex: 1, border: '1px solid #e6e9ef', borderRadius: 6, padding: '6px 10px', fontSize: 13, outline: 'none' }}
          />
          <button
            onClick={handleAdd}
            style={{ padding: '6px 14px', background: '#6161ff', color: 'white', borderRadius: 6, fontSize: 13, fontWeight: 500 }}
          >
            Add
          </button>
        </div>
        {editingColor === '__new__' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '8px', marginTop: 4, background: '#f6f7fb', borderRadius: 6 }}>
            {LABEL_COLORS.map((c) => (
              <div key={c} onClick={() => { setNewColor(c); setEditingColor(null); }}
                style={{ width: 24, height: 24, borderRadius: '50%', background: c, cursor: 'pointer', border: c === newColor ? '2px solid #323338' : '2px solid transparent' }}
              />
            ))}
          </div>
        )}

        {/* Save / Cancel */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: '8px 20px', background: '#f0f0ff', color: '#6161ff', borderRadius: 6, fontSize: 14, fontWeight: 500 }}>
            Cancel
          </button>
          <button onClick={handleSave} style={{ padding: '8px 20px', background: '#6161ff', color: 'white', borderRadius: 6, fontSize: 14, fontWeight: 500 }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
